import { supabase } from '../services/supabaseClient';
import { serviceRoleClient } from '../services/serviceRoleClient';

const cache = new Map();

export const parseCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const coordKey = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

const loadGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.DistanceMatrixService) return Promise.resolve(window.google.maps);
  if (window.__rsGoogleMapsPromise) return window.__rsGoogleMapsPromise;

  window.__rsGoogleMapsPromise = (async () => {
    const client = serviceRoleClient || supabase;
    const { data, error } = await client.rpc('get_google_api_key');
    const key = typeof data === 'string' ? data.trim() : '';
    if (error || !key || key.includes('your_google') || key.includes('YOUR_ACTUAL')) {
      throw new Error('Google Maps key missing');
    }

    await new Promise((resolve, reject) => {
      const existing = document.getElementById('rs-google-maps-js');
      if (existing) {
        if (window.google?.maps?.Map) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google Maps failed')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.id = 'rs-google-maps-js';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=en&region=IN`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(script);
    });

    if (!window.google?.maps?.DistanceMatrixService) throw new Error('Google Maps unavailable');
    return window.google.maps;
  })();

  return window.__rsGoogleMapsPromise;
};

const googleMatrix = (gmaps, origin, dests) =>
  new Promise((resolve) => {
    const service = new gmaps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [{ lat: origin.lat, lng: origin.lng }],
        destinations: dests.map((d) => ({ lat: d.lat, lng: d.lng })),
        travelMode: gmaps.TravelMode.DRIVING,
        unitSystem: gmaps.UnitSystem.METRIC,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.rows?.[0]?.elements) {
          resolve(null);
          return;
        }
        const elements = result.rows[0].elements;
        const byId = {};
        dests.forEach((d, i) => {
          const el = elements[i];
          if (el?.status !== 'OK' || el.distance?.value == null) return;
          byId[d.id] = {
            km: Math.round((el.distance.value / 1000) * 10) / 10,
            min: el.duration?.value != null ? Math.round(el.duration.value / 60) : null,
          };
        });
        resolve(byId);
      },
    );
  });

const osrmTable = async (origin, dests) => {
  const coords = [`${origin.lng},${origin.lat}`, ...dests.map((d) => `${d.lng},${d.lat}`)].join(';');
  const url = `https://router.project-osrm.org/table/v1/driving/${coords}?sources=0&annotations=distance,duration`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const json = await res.json();
  const distances = json?.distances?.[0];
  const durations = json?.durations?.[0];
  if (!Array.isArray(distances)) return null;
  const byId = {};
  dests.forEach((d, i) => {
    const meters = distances[i + 1];
    if (meters == null || !Number.isFinite(meters)) return;
    byId[d.id] = {
      km: Math.round((meters / 1000) * 10) / 10,
      min: durations?.[i + 1] != null ? Math.round(durations[i + 1] / 60) : null,
    };
  });
  return byId;
};

/** Driving km from origin → each dest (Google Maps route, OSRM fallback). */
export async function drivingDistancesById(origin, dests) {
  const from = parseCoord(origin?.lat ?? origin?.latitude, origin?.lng ?? origin?.longitude);
  const points = (dests || [])
    .map((d) => {
      const c = parseCoord(d.lat ?? d.latitude, d.lng ?? d.longitude);
      if (!c) return null;
      return { id: String(d.id), lat: c.lat, lng: c.lng };
    })
    .filter(Boolean);
  if (!from || !points.length) return {};

  const pending = [];
  const out = {};
  points.forEach((p) => {
    const key = `${coordKey(from.lat, from.lng)}>${coordKey(p.lat, p.lng)}`;
    const hit = cache.get(key);
    if (hit) out[p.id] = hit;
    else pending.push({ ...p, key });
  });
  if (!pending.length) return out;

  const chunks = [];
  for (let i = 0; i < pending.length; i += 24) chunks.push(pending.slice(i, i + 24));

  for (const chunk of chunks) {
    let byId = null;
    try {
      const gmaps = await loadGoogleMaps();
      byId = await googleMatrix(gmaps, from, chunk);
    } catch {
      byId = null;
    }
    if (!byId) {
      try {
        byId = await osrmTable(from, chunk);
      } catch {
        byId = null;
      }
    }
    chunk.forEach((p) => {
      const row = byId?.[p.id];
      if (!row) return;
      cache.set(p.key, row);
      out[p.id] = row;
    });
  }
  return out;
}
